/**
 * Keys the app parks in sessionStorage for the length of one signed-in session.
 *
 * Everything listed here describes *who is currently signed in* rather than a
 * durable preference, so it has to disappear the moment they sign out. A tab is
 * routinely handed on — one person signs out, the next signs in — and MSAL's
 * logout does not reload the page, so whatever is left behind is silently
 * inherited by the next user instead of being reset.
 */

// The workspace the user came in through. The API client sends it as
// X-Tenant-Slug on every request; inherited by the wrong user it pins them to a
// dealership they may have no membership in, and the backend answers 403.
export const WORKSPACE_SLUG_KEY = 'pp_workspace_slug';

// Set once an admin has been sent to the admin console, so the bounce happens
// only on the first load of their session and not on every return to the app.
export const ADMIN_SEEN_KEY = 'pp_admin_seen';

// Where the user was when their session expired (path + query). Signing back in —
// through the prompt, or through /login/:slug after a reload — returns them there.
export const RETURN_PATH_KEY = 'pp_return_path';

// The v1 "open this unit on that tab" handoff: written before sending the user
// to `/`, read (and removed) by the inventory once its data is in. It is a deep
// link, so the admin first-load bounce leaves it alone (QA T-11).
export const PENDING_UNIT_KEY = 'pendingOpenUnit';

// The visitor chose "← Back to IronSight" on a sign-in screen: for the rest of
// this visit `/` shows the landing page instead of resuming at the saved
// workspace's sign-in (QA L-10, L-13). Forgotten when they start a sign-in.
export const STAY_ON_LANDING_KEY = 'pp_stay_on_landing';

export function setStayOnLanding() {
    try { sessionStorage.setItem(STAY_ON_LANDING_KEY, '1'); } catch (e) { /* storage blocked: this navigation still stays */ }
}

export function clearStayOnLanding() {
    try { sessionStorage.removeItem(STAY_ON_LANDING_KEY); } catch (e) { /* nothing stored */ }
}

const SESSION_SCOPED_KEYS = [WORKSPACE_SLUG_KEY, ADMIN_SEEN_KEY, RETURN_PATH_KEY, STAY_ON_LANDING_KEY];

/** Forget everything that belongs to the session that is ending. */
export function clearSessionScopedState() {
    SESSION_SCOPED_KEYS.forEach((key) => {
        // Storage can be unavailable (private mode, blocked cookies). The writes
        // are guarded the same way, so a failure here means there was nothing
        // stored to begin with.
        try { sessionStorage.removeItem(key); } catch (e) { /* nothing to clear */ }
    });
}
